// Feature Mode Utility
// Provides helpers to get the effective mode for a specific module from Feature Settings

import type { FeatureMode } from '@/types/features';

const FEATURE_STORAGE_KEY = 'feature-settings';

// Map of route paths to feature module IDs
export const routeToModuleMap: Record<string, string> = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/assets': 'assets',
  '/transactions/additions': 'additions',
  '/transactions/depreciation': 'depreciation',
  '/transactions/revaluation': 'revaluation',
  '/checkinout': 'checkinout',
  '/checkinout/calendar': 'checkinout',
  '/checkinout/settings': 'checkinout',
  '/compliance': 'compliance',
  '/compliance/settings': 'compliance',
  '/audits/dashboard': 'audits',
  '/audits/active': 'audits',
  '/audits/scheduled': 'audits',
  '/audits/history': 'audits',
  '/audits/create': 'audits',
  '/exceptions': 'audits',
  '/insights': 'insights',
  '/enquiry': 'assetHistory',
  '/audit': 'auditTrail',
  '/tasks': 'workItems',
  '/tasks/home': 'workItems',
  '/administration/imports': 'importAssets',
  '/administration/import-transactions': 'importTransactions',
  '/administration/locations': 'locations',
  '/administration/categories': 'categories',
  '/administration/equipment': 'equipment',
  '/administration/disposal-reasons': 'disposalReasons',
  '/administration/teams-permissions': 'teamSettings',
  '/settings/features': 'featureSettings', // Feature Settings page itself - always available
  '/settings/asset-options': 'assetOptions', // Asset Options - always available
  '/help': 'help',
};

// Get the module ID for a given route path
export function getModuleIdForRoute(pathname: string): string | null {
  // Direct match
  if (routeToModuleMap[pathname]) {
    return routeToModuleMap[pathname];
  }
  
  // Check for asset detail pages (e.g., /assets/123)
  if (pathname.startsWith('/assets/')) {
    return 'assets';
  }
  
  // Check for audit detail pages (e.g., /audits/123)
  if (pathname.startsWith('/audits/')) {
    return 'audits';
  }
  
  // Check for task detail pages (e.g., /tasks/123)
  if (pathname.startsWith('/tasks/')) {
    return 'workItems';
  }
  
  return null;
}

// Get the effective mode for a module from Feature Settings
// Returns 'demo' if:
// - Module is not found
// - Module status is 'coming-soon'
// - Module has apiRequired flag set to true
// - Module mode is explicitly set to 'demo'
export function getModuleMode(moduleId: string): FeatureMode {
  try {
    const raw = localStorage.getItem(FEATURE_STORAGE_KEY);
    if (!raw) return 'demo';
    
    const parsed = JSON.parse(raw);
    const module = parsed.modules?.[moduleId];
    
    if (!module) return 'demo';
    
    // Force demo mode for modules that don't have API support
    if (module.status === 'coming-soon' || module.apiRequired) {
      return 'demo';
    }
    
    return module.mode ?? 'demo';
  } catch {
    return 'demo';
  }
}

// Check if a module can be switched to live mode
export function canModuleUseLiveMode(moduleId: string): boolean {
  try {
    const raw = localStorage.getItem(FEATURE_STORAGE_KEY);
    if (!raw) return true; // Default modules allow live mode unless marked otherwise
    
    const parsed = JSON.parse(raw);
    const module = parsed.modules?.[moduleId];
    
    if (!module) return true;
    
    // Cannot use live mode if coming-soon or apiRequired
    if (module.status === 'coming-soon' || module.apiRequired) {
      return false;
    }
    
    return true;
  } catch {
    return true;
  }
}

// Get the mode for a route path
export function getModeForRoute(pathname: string): FeatureMode {
  const moduleId = getModuleIdForRoute(pathname);
  if (!moduleId) return 'demo';
  return getModuleMode(moduleId);
}
